// First-party visitor + session identity — the browser half of
// internal/domain/visitor and the columns store_sessions keys on.
//
// COOKIES, not Web Storage — and that is the fix this file exists to carry, not
// an implementation detail. Reported live: the same person, one ordinary visit,
// showed up as TWO rows in store_sessions. Reproduced by opening a second tab
// (or clearing storage and reloading) — because sessionStorage's whole lifetime
// IS one tab, and localStorage never expires on its own, neither one has any
// notion of "the same person, a moment later" the way Shopify's own session
// contract needs: "a session ends after 30 minutes of inactivity." A cookie's
// rolling Max-Age is what expresses that, AND it is shared across every tab of
// one browser for one origin — Web Storage is partitioned per tab, a cookie is
// not.
//
// What this does NOT fix: two different HOSTNAMES for the same store (a
// platform subdomain vs a connected custom domain, or www vs bare). A cookie is
// origin-scoped by the browser exactly like Storage was, so landing on the
// "wrong" host still mints a fresh identity — cookies cannot cross an origin
// boundary any more than localStorage could. Shopify avoids this case by
// redirecting every non-canonical host to the merchant's chosen domain, which
// is a separate, larger change (see CLAUDE.md §9, "domains.is_primary" — no
// canonical-domain redirect exists yet).
//
// Two rules this file exists to keep:
//   * The client never invents the VISITOR token. A self-chosen id would be a
//     way to inherit someone else's identity (and their recovery mail). The
//     SESSION id is different — it names a VISIT, not a person — so the client
//     still mints it itself, exactly as before.
//   * The client never sends an email. Identity is attached server-side on
//     paths the server can vouch for (newsletter sign-up, checkout contact,
//     login).
//
// Cookie names are host-scoped (sanitised — ":" is not a legal cookie-name
// character) for the reason the old storage keys were: two independent dev
// stacks on different ports must not share an identity. This is unrelated to
// `?preview_store=` on one shared dev origin, which devstore.ts already scopes
// with its own per-tab sessionStorage key.

// Computed inside the accessors, never at module scope: lib/api imports this
// module, and lib/api is deliberately importable in a plain Node test env where
// `location` does not exist (see test/navigation.test.ts).

/** This host as a cookie/storage-key fragment. Every host-scoped key the storefront
 *  writes (dk_visitor_, dk_sess_, dk_match_) goes through this one sanitiser. */
export function hostFragment(): string {
  return location.host.replace(/[^a-zA-Z0-9_-]/g, "_")
}

/** Pure: find one cookie's value in a raw `document.cookie`-shaped string.
 *  Exported so the parsing itself is testable in plain Node — this file's
 *  `document`/`location` reads are not. */
export function parseCookie(raw: string, name: string): string {
  const prefix = name + "="
  for (const part of raw.split(";")) {
    const c = part.trim()
    if (c.startsWith(prefix)) return decodeURIComponent(c.slice(prefix.length))
  }
  return ""
}

/** Pure: the Set-Cookie-shaped string a write assigns to `document.cookie`.
 *  SameSite=Lax so it still rides a top-level RETURN navigation (a PayPal /
 *  Airwallex redirect back into checkout), which Strict would drop. `secure`
 *  is a parameter rather than read here — over http the write is silently
 *  REJECTED by the browser if Secure is present, which reads as "cookies are
 *  broken" rather than as a scheme mismatch, so the caller decides from
 *  `location.protocol`. */
export function cookieString(name: string, value: string, maxAgeSeconds: number, secure: boolean): string {
  return `${name}=${encodeURIComponent(value)}; Max-Age=${maxAgeSeconds}; Path=/; SameSite=Lax${secure ? "; Secure" : ""}`
}

/** Throws exactly when `document.cookie` itself does (a locked-down sandbox) —
 *  callers decide how to degrade, matching the rest of this file's try/catch
 *  placement rather than swallowing it here. */
function readCookie(name: string): string {
  return parseCookie(document.cookie, name)
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
  document.cookie = cookieString(name, value, maxAgeSeconds, location.protocol === "https:")
}

const key = () => `dk_visitor_${hostFragment()}`
// ~1 year, matching a typical first-party visitor cookie's job: recognise a
// RETURNING browser. Refreshed on every read that finds a value, so an active
// shopper's identity keeps rolling forward rather than expiring exactly a year
// after their FIRST visit and never again after that.
const VISITOR_TTL_SECONDS = 365 * 24 * 60 * 60

let cached: string | null = null

/** The stored token, or "" when this browser has none yet. */
export function visitorToken(): string {
  if (cached !== null) return cached
  try {
    const v = readCookie(key())
    if (v) writeCookie(key(), v, VISITOR_TTL_SECONDS) // roll the expiry forward
    cached = v
  } catch {
    // Private mode with storage blocked: the visitor stays anonymous, which
    // degrades recovery for them and breaks nothing.
    cached = ""
  }
  return cached
}

// The per-VISIT id — a cookie with a rolling Max-Age, refreshed on use, that
// dies on its own after SESSION_TTL_SECONDS of no activity. That is exactly
// Shopify's own session contract, and it is also what keeps this one
// consent-free unlike the visitor token above: it cannot outlive a short
// window of inactivity, and it carries no more than the request it rides on.
//
// It lives HERE rather than in lib/tracking/firstparty.ts, where it started,
// because the beacon and the API client must mint the SAME id: the beacon writes
// the session's browsing log while the API client's header is what binds a cart
// to it, and two ids would leave every cart detail with an empty activity log.
// Both now read this one.
const sessKey = () => `dk_sess_${hostFragment()}`
const SESSION_TTL_SECONDS = 30 * 60

// The cookie's Max-Age is extended on (almost) every read — that IS the rolling
// window — but not on literally every call: a single page load can fire a dozen
// API requests within a second, and rewriting document.cookie that often is
// pure churn for a clock that only needs minute-level precision against a
// half-hour window. Refreshing at most this often keeps the semantics at the
// cost of one extra cookie write per minute of activity, not per request.
const SESSION_REFRESH_MIN_GAP_MS = 60_000

let sess: string | null = null
let sessLastRefreshAt = 0

/** This visit's id. Survives a reload, a new tab, and a brief absence — dies on
 *  its own after 30 minutes with nothing to renew it. */
export function sessionID(): string {
  try {
    const now = Date.now()
    if (sess === null) {
      sess = readCookie(sessKey())
      if (!sess) sess = Math.random().toString(36).slice(2) + Date.now().toString(36)
      writeCookie(sessKey(), sess, SESSION_TTL_SECONDS)
      sessLastRefreshAt = now
    } else if (now - sessLastRefreshAt > SESSION_REFRESH_MIN_GAP_MS) {
      writeCookie(sessKey(), sess, SESSION_TTL_SECONDS)
      sessLastRefreshAt = now
    }
  } catch {
    // Storage blocked: every request looks like its own session, so the server
    // records a one-request visit rather than nothing. Degrades, never breaks.
    sess = ""
  }
  return sess ?? ""
}

/**
 * Header bag for the API client: who this browser is, and which visit this is.
 * Either is omitted when empty — never an empty header.
 */
export function identityHeaders(): Record<string, string> {
  const h: Record<string, string> = {}
  const t = visitorToken()
  if (t) h["X-DK-Visitor"] = t
  const s = sessionID()
  if (s) h["X-DK-Session"] = s
  // NO client-context header. This used to send referrer/landing path/language/
  // timezone/screen as X-DK-Context on whichever call went first, so presence
  // cost no extra request. lib/sessionping.ts posts the same facts in its own
  // body instead, and that mechanism won on the database rather than on request
  // count — see the note above presenceGate in the API's session.go. Sending the
  // header as well would be a second writer of one row, so the reader was
  // deleted and this had to go with it.
  return h
}

let minting: Promise<void> | null = null

/**
 * Mint the token if this browser has none. Called once per load, at idle, and
 * ONLY once the tracking gate is open — the token is a persistent identifier, so
 * it belongs on the same side of the cookie banner as the pixels.
 *
 * Best-effort in every direction: a failure leaves the visitor anonymous.
 */
export function ensureVisitor(): Promise<void> {
  if (visitorToken()) return Promise.resolve()
  if (minting) return minting
  // Imported lazily so this module never has to import ./api at evaluation time:
  // api.ts reads visitorHeader() from here, and a static cycle between the two
  // is the kind of thing that only breaks once the bundler reorders modules.
  minting = import("./api")
    .then(m => m.api.ensureVisitor())
    .then(res => {
      const token = res.visitor?.token
      if (!token) return
      cached = token
      try {
        writeCookie(key(), token, VISITOR_TTL_SECONDS)
      } catch {
        /* the header still works for this page load */
      }
    })
    .catch(() => {
      /* identity is an enrichment; never surface it to the shopper */
    })
  return minting
}
